import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT, documentTextButtonSx } from '../constants/documentTheme';
import { partnerOxbloodBtnSx } from './PartnerListChrome';
import PartnerInventoryPickerDialog from './PartnerInventoryPickerDialog';
import PartnerCatalogItemDialog from './PartnerCatalogItemDialog';
import PartnerTripDeliveryOptions from './PartnerTripDeliveryOptions';
import { CATALOG_CONFIGS } from '../utils/partnerCatalog';

const PARTNER_AUTH = { usePartnerAuth: true };

const PICKER_COPY = {
  inventory: {
    title: 'partnerAddons.offerInventory',
    empty: 'partnerAddons.inventoryEmpty',
    search: 'partnerInventory.pickerSearch',
  },
  addon: {
    title: 'partnerAddons.offerAddon',
    empty: 'partnerAddons.addonsEmpty',
    search: 'partnerAddonCatalog.pickerSearch',
  },
};

const AddonRow = ({ testId, name, meta, amount, currency, children }) => (
  <Box
    data-testid={testId}
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
      py: 1.5,
      borderBottom: `1px solid ${DOCUMENT.rule}`,
      flexWrap: 'wrap',
      alignItems: 'center',
    }}
  >
    <Box>
      <Typography sx={{ fontSize: 15 }}>{name}</Typography>
      <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>{meta}</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {Number(amount || 0).toFixed(2)} {currency}
      </Typography>
      {children}
    </Box>
  </Box>
);

const PartnerTripAddOns = ({
  tripId,
  isActive = true,
  audience = 'shared',
  forGroup = false,
  actionsDisabled = false,
  launch = null,
  onLaunchHandled,
}) => {
  const { t } = useTranslation();
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [pickerKind, setPickerKind] = useState(null);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerItemId, setPickerItemId] = useState('');
  const [offering, setOffering] = useState(false);
  const [quantity, setQuantity] = useState('1');
  const [pricingMode, setPricingMode] = useState('charge');
  const [deliveryMode, setDeliveryMode] = useState('charge');
  const [createOpen, setCreateOpen] = useState(false);
  const [notice, setNotice] = useState('');

  const fetchLedger = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/package-booking/partner/trips/${tripId}/addons`, PARTNER_AUTH);
      setLedger(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerAddons.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [tripId, t]);

  useEffect(() => {
    if (isActive) fetchLedger();
  }, [isActive, fetchLedger]);

  const resetDeliveryOptions = useCallback(() => {
    setQuantity('1');
    setPricingMode('charge');
    setDeliveryMode('charge');
  }, []);

  const openPicker = useCallback(async (kind, initialQuery = '') => {
    try {
      setError('');
      const response = await api.get(`${CATALOG_CONFIGS[kind].endpoint}/items`, PARTNER_AUTH);
      setPickerItems(response.data.items || []);
      setPickerItemId('');
      setPickerQuery(initialQuery);
      resetDeliveryOptions();
      setPickerKind(kind);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerAddons.offerFailed'));
    }
  }, [resetDeliveryOptions, t]);

  useEffect(() => {
    if (!isActive || !launch?.kind) return undefined;
    let cancelled = false;
    (async () => {
      await openPicker(launch.kind, launch.query || '');
      if (!cancelled) onLaunchHandled?.();
    })();
    return () => { cancelled = true; };
  }, [isActive, launch, onLaunchHandled, openPicker]);

  const closePicker = () => {
    setPickerKind(null);
    setPricingMode('charge');
  };

  const openCreate = () => {
    setError('');
    resetDeliveryOptions();
    setCreateOpen(true);
  };

  const addCatalogItemToTrip = async (kind, itemId) => {
    const free = pricingMode === 'free';
    return api.post(
      `${CATALOG_CONFIGS[kind].endpoint}/items/${itemId}/offer-on-trip`,
      {
        tripId: Number(tripId),
        free,
        quantity: free ? 1 : Math.max(1, Number(quantity) || 1),
        mode: deliveryMode,
        ...(forGroup ? { forGroup: true } : {}),
        ...(audience === 'personal' && !forGroup ? { audience: 'personal' } : {}),
      },
      PARTNER_AUTH
    );
  };

  const offerPickedItem = async () => {
    try {
      setOffering(true);
      setError('');
      const response = await addCatalogItemToTrip(pickerKind, pickerItemId);
      closePicker();
      if (forGroup) {
        setNotice(t('partnerAddons.offeredToEveryone', { count: response?.data?.travelerCount || 0 }));
      }
      await fetchLedger();
    } catch (err) {
      setError(err.response?.data?.error || t('partnerAddons.offerFailed'));
    } finally {
      setOffering(false);
    }
  };

  /** The new add-on is already saved to the catalog, so a failed trip step only reports the error. */
  const addCreatedAddon = async (addon) => {
    setCreateOpen(false);
    try {
      setError('');
      const response = await addCatalogItemToTrip('addon', addon.id);
      if (forGroup) {
        setNotice(t('partnerAddons.offeredToEveryone', { count: response?.data?.travelerCount || 0 }));
      }
    } catch (err) {
      setError(err.response?.data?.error || t('partnerAddons.createdNotAdded', { name: addon.name }));
      return;
    }
    await fetchLedger();
  };

  const requestPayment = async ({ lineItemId, packageItemId, offerId } = {}) => {
    try {
      setBusyId(lineItemId || packageItemId || (offerId && `offer-${offerId}`) || 'tab');
      setError('');
      let body = {};
      if (lineItemId) body = { lineItemId };
      else if (packageItemId) body = { packageItemId };
      else if (offerId) body = { offerId };
      const response = await api.post(
        `/package-booking/partner/trips/${tripId}/addons/request-payment`,
        body,
        PARTNER_AUTH
      );
      setLedger(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerAddons.requestFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const removeLine = async (lineItemId) => {
    try {
      setBusyId(`remove-line-${lineItemId}`);
      setError('');
      const response = await api.delete(
        `/package-booking/partner/trips/${tripId}/addons/lines/${lineItemId}`,
        PARTNER_AUTH
      );
      setLedger(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerAddons.removeFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const removeOffer = async (offerId) => {
    try {
      setBusyId(`remove-${offerId}`);
      setError('');
      const response = await api.delete(
        `/package-booking/partner/trips/${tripId}/addons/offers/${offerId}`,
        PARTNER_AUTH
      );
      setLedger(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerAddons.removeFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const catalog = ledger?.catalog || [];
  const offers = useMemo(() => ledger?.offers || [], [ledger]);
  const offerLineIds = useMemo(
    () => new Set(offers.map((offer) => Number(offer.line_item_id)).filter(Boolean)),
    [offers]
  );
  const orphans = (ledger?.items || []).filter(
    (item) => !item.package_item_id && !offerLineIds.has(Number(item.id))
  );
  const totals = ledger?.totals || { unpaidTotal: 0, paidTotal: 0, dueCount: 0 };
  const statusLabel = (status) => {
    if (status === 'paid') return t('partnerAddons.purchased');
    if (status === 'unpaid') return t('partnerAddons.pending');
    return t('partnerAddons.available');
  };
  const offerStatusLabel = (offer) => {
    if (offer.status === 'available') return t('partnerAddons.offeredToTraveler');
    return statusLabel(offer.status);
  };
  const dayPrefix = (dayNumber) => (dayNumber ? `${t('partnerAddons.day', { n: dayNumber })} · ` : '');
  const pickerCopy = pickerKind ? PICKER_COPY[pickerKind] : PICKER_COPY.inventory;

  return (
    <Box data-testid="partner-trip-addons">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1, alignItems: 'flex-end' }}>
        <Box>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
            {t('partnerAddons.title')}
          </Typography>
          <Typography sx={{ fontSize: 14, color: DOCUMENT.ink3 }}>
            {t('partnerAddons.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button sx={partnerOxbloodBtnSx} onClick={openCreate} disabled={actionsDisabled} data-testid="partner-addons-new">
            {t('partnerAddons.newAddon')}
          </Button>
          <Button sx={partnerOxbloodBtnSx} onClick={() => openPicker('addon')} disabled={actionsDisabled} data-testid="partner-addons-from-addons">
            {t('partnerAddons.fromAddons')}
          </Button>
          <Button sx={partnerOxbloodBtnSx} onClick={() => openPicker('inventory')} disabled={actionsDisabled} data-testid="partner-addons-from-inventory">
            {t('partnerAddons.fromInventory')}
          </Button>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
      {loading ? (
        <Typography sx={{ color: DOCUMENT.ink3 }}>{t('common.loading')}</Typography>
      ) : (
        <>
          {catalog.length === 0 && orphans.length === 0 && offers.length === 0 ? (
            <Typography sx={{ color: DOCUMENT.ink3 }}>{t('partnerAddons.empty')}</Typography>
          ) : (
            <Box>
              {catalog.map((item) => (
                <AddonRow
                  key={item.id}
                  testId={`partner-addon-${item.line_item_id || item.id}`}
                  name={item.name}
                  meta={`${dayPrefix(item.day_number)}${statusLabel(item.status)}`}
                  amount={item.additional_cost}
                  currency={item.currency}
                >
                  {item.status !== 'paid' && (
                    <Button
                      sx={documentTextButtonSx}
                      disabled={Boolean(busyId)}
                      onClick={() => requestPayment(
                        item.line_item_id
                          ? { lineItemId: item.line_item_id }
                          : { packageItemId: item.id }
                      )}
                    >
                      {busyId === (item.line_item_id || item.id)
                        ? t('partnerAddons.requesting')
                        : t('partnerAddons.requestOne')}
                    </Button>
                  )}
                  {item.status !== 'paid' && item.line_item_id && (
                    <Button
                      sx={documentTextButtonSx}
                      disabled={Boolean(busyId)}
                      onClick={() => removeLine(item.line_item_id)}
                      data-testid={`partner-addon-${item.line_item_id}-remove`}
                    >
                      {t('partnerAddons.remove')}
                    </Button>
                  )}
                </AddonRow>
              ))}
              {offers.map((offer) => (
                <AddonRow
                  key={`offer-${offer.id}`}
                  testId={`partner-addon-offer-${offer.id}`}
                  name={offer.name}
                  meta={`${dayPrefix(offer.day_number)}${offerStatusLabel(offer)}`}
                  amount={offer.amount}
                  currency={offer.currency}
                >
                  {offer.status !== 'paid' && (
                    <>
                      <Button
                        sx={documentTextButtonSx}
                        disabled={Boolean(busyId)}
                        onClick={() => requestPayment(
                          offer.line_item_id ? { lineItemId: offer.line_item_id } : { offerId: offer.id }
                        )}
                        data-testid={`partner-addon-offer-${offer.id}-request`}
                      >
                        {busyId === (offer.line_item_id || `offer-${offer.id}`)
                          ? t('partnerAddons.requesting')
                          : t('partnerAddons.requestOne')}
                      </Button>
                      <Button
                        sx={documentTextButtonSx}
                        disabled={Boolean(busyId)}
                        onClick={() => removeOffer(offer.id)}
                        data-testid={`partner-addon-offer-${offer.id}-remove`}
                      >
                        {t('partnerAddons.remove')}
                      </Button>
                    </>
                  )}
                </AddonRow>
              ))}
              {orphans.map((item) => (
                <AddonRow
                  key={item.id}
                  testId={`partner-addon-${item.id}`}
                  name={item.name}
                  meta={item.payment_status === 'paid' ? t('partnerAddons.paid') : t('partnerAddons.unpaid')}
                  amount={item.amount}
                  currency={item.currency}
                >
                  {item.payment_status !== 'paid' && (
                    <>
                      <Button
                        sx={documentTextButtonSx}
                        disabled={Boolean(busyId)}
                        onClick={() => requestPayment({ lineItemId: item.id })}
                      >
                        {busyId === item.id ? t('partnerAddons.requesting') : t('partnerAddons.requestOne')}
                      </Button>
                      <Button
                        sx={documentTextButtonSx}
                        disabled={Boolean(busyId)}
                        onClick={() => removeLine(item.id)}
                        data-testid={`partner-addon-${item.id}-remove`}
                      >
                        {t('partnerAddons.remove')}
                      </Button>
                    </>
                  )}
                </AddonRow>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2.5, flexWrap: 'wrap', gap: 2 }}>
                <Typography sx={{ fontSize: 14 }}>
                  {t('partnerAddons.due', { amount: Number(totals.unpaidTotal || 0).toFixed(2) })}
                </Typography>
                {totals.dueCount > 1 && (
                  <Button
                    sx={partnerOxbloodBtnSx}
                    disabled={Boolean(busyId)}
                    onClick={() => requestPayment({})}
                  >
                    {busyId === 'tab' ? t('partnerAddons.requesting') : t('partnerAddons.requestTab')}
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </>
      )}

      <PartnerInventoryPickerDialog
        open={Boolean(pickerKind)}
        onClose={closePicker}
        items={pickerItems}
        selectedId={pickerItemId}
        onSelect={setPickerItemId}
        onConfirm={offerPickedItem}
        confirming={offering}
        title={t(pickerCopy.title)}
        confirmLabel={pickerItemId ? t('partnerAddons.addToTrip') : t(pickerCopy.title)}
        emptyLabel={t(pickerCopy.empty)}
        searchPlaceholder={t(pickerCopy.search)}
        initialQuery={pickerQuery}
        categoryKeyPrefix={`${CATALOG_CONFIGS[pickerKind || 'inventory'].i18n}.category`}
        showQuantity
        quantity={quantity}
        onQuantityChange={setQuantity}
        pricingMode={pricingMode}
        onPricingModeChange={setPricingMode}
        deliveryMode={deliveryMode}
        onDeliveryModeChange={setDeliveryMode}
        testIdPrefix={pickerKind === 'addon' ? 'trip-addons-catalog' : 'trip-addons-inventory'}
      />

      <PartnerCatalogItemDialog
        open={createOpen}
        kind="addon"
        onClose={() => setCreateOpen(false)}
        onSaved={addCreatedAddon}
        title={t('partnerAddons.newAddon')}
        saveLabel={t('partnerAddons.createAndAdd')}
        renderExtra={(form) => (
          <>
            <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>
              {t('partnerAddons.newAddonHint')}
            </Typography>
            <PartnerTripDeliveryOptions
              item={form.name.trim() ? form : null}
              pricingMode={pricingMode}
              onPricingModeChange={setPricingMode}
              deliveryMode={deliveryMode}
              onDeliveryModeChange={setDeliveryMode}
              showQuantity={form.priceFrequency !== 'one_time'}
              quantity={quantity}
              onQuantityChange={setQuantity}
              testIdPrefix="trip-addons-new"
            />
          </>
        )}
      />
    </Box>
  );
};

export default PartnerTripAddOns;
